/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Type-safe i18n Store & Translation Engine for Admin Portal (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import enDict from '../i18n/locales/en.json';
import viDict from '../i18n/locales/vi.json';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
  type TranslationKey,
  type TranslationParams,
  type LocaleMetadata,
  LOCALE_CONFIG,
} from '../i18n/types';

export const ADMIN_I18N_STORAGE_KEY = 'sophia_admin_locale';

// Dictionaries map
export const DICTIONARIES: Record<SupportedLocale, Record<string, unknown>> = {
  en: enDict,
  vi: viDict,
  ja: enDict,
  ko: enDict,
  es: enDict,
  zh: enDict,
};

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return typeof current === 'string' ? current : undefined;
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    return key in params ? String(params[key]) : match;
  });
}

export function translate(
  locale: SupportedLocale,
  key: string,
  params?: TranslationParams,
  fallback?: string,
): string {
  const primaryDict = DICTIONARIES[locale] || DICTIONARIES[DEFAULT_LOCALE];
  const primaryVal = getNestedValue(primaryDict, key);

  if (primaryVal !== undefined) {
    return interpolate(primaryVal, params);
  }

  if (locale !== DEFAULT_LOCALE) {
    const fallbackVal = getNestedValue(DICTIONARIES[DEFAULT_LOCALE], key);
    if (fallbackVal !== undefined) {
      return interpolate(fallbackVal, params);
    }
  }

  return fallback !== undefined ? interpolate(fallback, params) : key;
}

export interface I18nState {
  currentLocale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (
    key: TranslationKey | (string & Record<never, never>),
    params?: TranslationParams,
    fallback?: string,
  ) => string;
  getMetadata: () => LocaleMetadata;
}

function getInitialLocale(): SupportedLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const stored = localStorage.getItem(ADMIN_I18N_STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as SupportedLocale;
  }

  const browserLang = navigator.language?.split('-')[0];
  if (browserLang && (SUPPORTED_LOCALES as readonly string[]).includes(browserLang)) {
    return browserLang as SupportedLocale;
  }

  return DEFAULT_LOCALE;
}

export const useI18nStore = create<I18nState>((set, get) => ({
  currentLocale: getInitialLocale(),

  setLocale: (newLocale: SupportedLocale) => {
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(newLocale)) {
      console.warn(`[i18n] Unsupported locale: ${newLocale}, ignoring.`);
      return;
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem(ADMIN_I18N_STORAGE_KEY, newLocale);
      window.dispatchEvent(
        new CustomEvent('sophia:admin-locale-changed', { detail: { locale: newLocale } }),
      );
    }

    set({ currentLocale: newLocale });
  },

  t: (key, params, fallback) => {
    const { currentLocale } = get();
    return translate(currentLocale, key, params, fallback);
  },

  getMetadata: () => {
    const { currentLocale } = get();
    return LOCALE_CONFIG[currentLocale] || LOCALE_CONFIG[DEFAULT_LOCALE];
  },
}));

export const t = (
  key: TranslationKey | (string & Record<never, never>),
  params?: TranslationParams,
  fallback?: string,
) => useI18nStore.getState().t(key, params, fallback);

export function useI18n() {
  const currentLocale = useI18nStore((state) => state.currentLocale);
  const setLocale = useI18nStore((state) => state.setLocale);
  const t = useI18nStore((state) => state.t);
  const getMetadata = useI18nStore((state) => state.getMetadata);

  return {
    locale: currentLocale,
    currentLocale,
    setLocale,
    t,
    getMetadata,
  };
}
